import type { UserDataQuery } from '~~/@types/generated/backend/graphql-schema-types'

import { datadogLogs } from '@datadog/browser-logs'
import { DATADOG_STATES } from '~~/layers/datadog/constants/datadog'

const getConnectivityText = (isOnline: boolean) =>
  isOnline ? 'online' : 'offline'

export default defineNuxtPlugin({
  dependsOn: ['apollo'],
  name: 'datadogInit',
  setup: () => {
    const {
      datadog: { container, enabled, env, token },
      licensedTerritory,
      namespace,
      version: release,
    } = useRuntimeConfig().public

    if (String(enabled) !== 'true' || datadogLogs.getInitConfiguration()) return

    const online = useOnline()
    const { isLoggedIn } = useAuth()
    const { $apollo } = useNuxtApp()

    const isPlayerStatusPending = useState<boolean>(() => false)
    const playerId = useState<Maybe<string>>(DATADOG_STATES.Player, () => null)

    datadogLogs.init({
      clientToken: token,
      env,
      forwardErrorsToLogs: true,
      service: 'nuxt3-frontend-client',
      site: 'datadoghq.eu',
      version: release,
    })

    datadogLogs.setGlobalContext({
      connectivity: getConnectivityText(online.value),
      containerName: container,
      host: 'browser',
      licensedTerritory,
      namespace,
    })

    watch(online, () => {
      datadogLogs.setGlobalContextProperty(
        'connectivity',
        getConnectivityText(online.value),
      )
    })

    watchImmediate(
      () => isLoggedIn.value,
      async () => {
        if (isPlayerStatusPending.value) return

        try {
          if (isLoggedIn.value && playerId.value === null) {
            isPlayerStatusPending.value = true

            const { data } = await $apollo.defaultClient.query<UserDataQuery>({
              fetchPolicy: 'cache-first',
              query: UserDataDocument,
            })

            playerId.value = data.player.id
          } else if (!isLoggedIn.value && playerId.value !== null) {
            playerId.value = null
          }
        } catch {
          // ignore
        } finally {
          isPlayerStatusPending.value = false
        }
      },
    )

    watchImmediate(playerId, (newPlayerId) => {
      if (newPlayerId) {
        datadogLogs.setGlobalContextProperty('playerId', playerId.value)
      } else {
        datadogLogs.removeGlobalContextProperty('playerId')
      }
    })
  },
})
